/**
 * Deal Lifecycle Utilities
 *
 * `deals.stage` is NULLABLE in Postgres, and `Deal` is now a codegen-derived
 * projection that says so (#2633) — the hand-written type used to claim it was
 * required, which is why these filters read it unguarded. A stage-less deal
 * belongs to NO lifecycle bucket, which is exactly what `Array.includes` already
 * did with `undefined` at runtime; the guards below just make that legible.
 */

import type { Deal, DealStage } from "@/lib/types/database/deals";
import {
  SALES_PIPELINE_STAGES,
  CLOSED_DEAL_STAGES,
  LOST_STAGES,
  DEFAULT_LOST_STAGE,
  isLostStage,
  hasDealStage,
} from "@/lib/constants/deal-stages";

export function getSalesPipelineDeals(deals: Deal[]): Deal[] {
  return deals.filter((d) => hasDealStage(d.stage) && SALES_PIPELINE_STAGES.includes(d.stage));
}

export function getClosedDeals(deals: Deal[]): Deal[] {
  return deals.filter((d) => hasDealStage(d.stage) && CLOSED_DEAL_STAGES.includes(d.stage));
}

export function getDealsNotInvoiced(deals: Deal[]): Deal[] {
  return deals.filter(
    (d) =>
      hasDealStage(d.stage) &&
      CLOSED_DEAL_STAGES.includes(d.stage) &&
      !["invoiced", "paid"].includes(d.stage)
  );
}

export function getDealsReadyToInvoice(deals: Deal[]): Deal[] {
  return deals.filter((d) => d.stage === "completed");
}

export function getInvoicedDeals(deals: Deal[]): Deal[] {
  return deals.filter((d) => hasDealStage(d.stage) && ["invoiced", "paid"].includes(d.stage));
}

export function getPaidDeals(deals: Deal[]): Deal[] {
  return deals.filter((d) => d.stage === "paid");
}

export function getLostDeals(deals: Deal[]): Deal[] {
  return deals.filter((d) => hasDealStage(d.stage) && isLostStage(d.stage));
}

const LOST_TRANSITION_TARGETS: DealStage[] = [...LOST_STAGES];

export function canTransitionStage(
  currentStage: DealStage,
  nextStage: DealStage
): boolean {
  if (isLostStage(currentStage)) {
    return false;
  }
  if (isLostStage(nextStage)) {
    return LOST_TRANSITION_TARGETS.includes(nextStage);
  }

  const validTransitions: Record<DealStage, DealStage[]> = {
    lead: ["qualified", ...LOST_TRANSITION_TARGETS],
    qualified: ["discovery", ...LOST_TRANSITION_TARGETS],
    discovery: ["proposal", ...LOST_TRANSITION_TARGETS],
    proposal: ["negotiation", ...LOST_TRANSITION_TARGETS],
    negotiation: ["contract_signed", ...LOST_TRANSITION_TARGETS],
    contract_signed: ["deal_won", ...LOST_TRANSITION_TARGETS],
    deal_won: ["hand_off", ...LOST_TRANSITION_TARGETS],
    hand_off: ["started", ...LOST_TRANSITION_TARGETS],
    started: ["in_progress", ...LOST_TRANSITION_TARGETS],
    in_progress: ["completed", ...LOST_TRANSITION_TARGETS],
    completed: ["invoiced", ...LOST_TRANSITION_TARGETS],
    invoiced: ["paid", ...LOST_TRANSITION_TARGETS],
    paid: [...LOST_TRANSITION_TARGETS],
    sm_lost_no_savings: [],
    sm_lost_savings_customer_rejected: [],
    wtc_lost_solution_rejected: [],
    wtc_lost_over_budget: [],
    wtc_lost_no_budget: [],
  };

  return validTransitions[currentStage]?.includes(nextStage) ?? false;
}

export function getNextStage(currentStage: DealStage): DealStage | null {
  const nextStages: Record<DealStage, DealStage | null> = {
    lead: "qualified",
    qualified: "discovery",
    discovery: "proposal",
    proposal: "negotiation",
    negotiation: "contract_signed",
    contract_signed: "deal_won",
    deal_won: "hand_off",
    hand_off: "started",
    started: "in_progress",
    in_progress: "completed",
    completed: "invoiced",
    invoiced: "paid",
    paid: null,
    sm_lost_no_savings: null,
    sm_lost_savings_customer_rejected: null,
    wtc_lost_solution_rejected: null,
    wtc_lost_over_budget: null,
    wtc_lost_no_budget: null,
  };

  return nextStages[currentStage] ?? null;
}

export function getStageLabelForDisplay(stage: DealStage): string {
  const labels: Record<DealStage, string> = {
    lead: "Lead",
    qualified: "Qualified",
    discovery: "Discovery",
    proposal: "Proposal",
    negotiation: "Negotiation",
    contract_signed: "Contract Signed",
    deal_won: "Deal Won",
    hand_off: "Hand Off",
    started: "Started",
    in_progress: "In Progress",
    completed: "Completed",
    invoiced: "Invoiced",
    paid: "Paid",
    sm_lost_no_savings: "SM Lost — No Savings Found",
    sm_lost_savings_customer_rejected: "SM Lost — Savings Found, Customer Rejected",
    wtc_lost_solution_rejected: "WTC Lost — Solution Rejected",
    wtc_lost_over_budget: "WTC Lost — Over Budget",
    wtc_lost_no_budget: "WTC Lost — No Budget",
  };

  return labels[stage] ?? stage;
}

export function getStageCategory(
  stage: DealStage
): "sales_pipeline" | "closed_deal" | "lost" {
  if (isLostStage(stage)) return "lost";
  if (SALES_PIPELINE_STAGES.includes(stage)) return "sales_pipeline";
  return "closed_deal";
}

export function getStagesByCategory(
  category: "sales_pipeline" | "closed_deal" | "lost"
): DealStage[] {
  switch (category) {
    case "sales_pipeline":
      return SALES_PIPELINE_STAGES;
    case "closed_deal":
      return CLOSED_DEAL_STAGES;
    case "lost":
      return LOST_STAGES;
  }
}

export { DEFAULT_LOST_STAGE };
